import React, { useState, useMemo, useCallback, lazy, Suspense } from "react";
import axios from "axios";
import {
  CssBaseline,
  Container,
  Grid,
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
} from "@material-ui/core";
import "./index.css";
import "./RegistrationEmail.css";
import BackgroundImg from "../components/UI/BackgroundImg";

const Logo = lazy(() => import("../components/UI/Logo"));

const SPECIAL_HIDE_LOGO_EVENT_ID = "6617f9d68d18254c64eac445";
const SPECIAL_HICL_LOGO_EVENT_IDS = new Set([
  "69175e8f27ca94001446d2f9",
  "68f6124464cab60013721242",
]);
const SPECIAL_HICL_LOGO_URL =
  "https://brrmedia-clients-uploads.s3.us-west-2.amazonaws.com/HICL/HICL_TRIG+V2.png";

function findWebsiteLogoPath(eventItem) {
  if (eventItem?.webcast?.branding?.logoSelection) {
    return eventItem?.webcast?.branding?.logoSelection?.path;
  }
  const logos = eventItem?.companies?.[0]?.logos || [];
  const websiteLogo = logos.find((l) => l?.type === "website");
  return websiteLogo?.upload?.path || null;
}

function computeLogoPadding(eventItem) {
  const padding = eventItem?.webcast?.branding?.logo?.padding;
  return padding && padding !== "" ? padding : "10px";
}

function resolveLogoSrc(eventItem, logoPath) {
  if (eventItem?.webcast?.branding?.logoSelection) {
    return eventItem?.webcast?.branding?.logoSelection?.path?.startsWith("http")
      ? eventItem?.webcast?.branding?.logoSelection?.path
      : `${process.env.REACT_APP_ASSETS_BASE_URL}/${eventItem?.webcast?.branding?.logoSelection?.path}`;
  }
  if (!logoPath) return null;
  if (SPECIAL_HICL_LOGO_EVENT_IDS.has(eventItem?._id)) return SPECIAL_HICL_LOGO_URL;
  return `${process.env.REACT_APP_ASSETS_BASE_URL}/${logoPath}`;
}

function resolveAssetPath(path) {
  if (!path) return null;
  return path.startsWith("http")
    ? path
    : `${process.env.REACT_APP_ASSETS_BASE_URL}/${path}`;
}

function Background({ eventItem }) {
  if (eventItem?.holdingSlide?.path) {
    return (
      <BackgroundImg
        path={resolveAssetPath(eventItem.holdingSlide.path)}
        img={true}
        colour={null}
      />
    );
  }

  const hbb = eventItem?.webcast?.branding?.holdingBlockBackground;
  if (hbb?.type) {
    const opacity = hbb.opacity != null ? hbb.opacity : 1;
    if (hbb.type === "image") {
      return hbb.image?.path ? (
        <BackgroundImg
          path={resolveAssetPath(hbb.image.path)}
          img={true}
          colour={null}
          opacity={opacity}
        />
      ) : null;
    }
    if (hbb.type === "video") {
      return hbb.video?.path ? (
        <BackgroundImg
          path={resolveAssetPath(hbb.video.path)}
          video={true}
          colour={null}
          opacity={opacity}
        />
      ) : null;
    }
    if (hbb.type === "color" && hbb.color) {
      return (
        <BackgroundImg
          path={null}
          img={false}
          colour={hbb.color}
          opacity={opacity}
        />
      );
    }
    return null;
  }

  // Legacy fallbacks — only when no typed holdingBlockBackground
  const pageBg = eventItem?.webcast?.branding?.background;
  if (pageBg?.type === "image" && pageBg.image?.path) {
    return (
      <BackgroundImg
        path={resolveAssetPath(pageBg.image.path)}
        img={true}
        colour={null}
      />
    );
  }
  if (pageBg?.type === "color" && pageBg.color) {
    return <BackgroundImg path={null} img={false} colour={pageBg.color} />;
  }

  const bg = eventItem?.webcast?.colour?.background;
  if (bg !== null && bg !== undefined && bg !== "") {
    return <BackgroundImg path={null} img={false} colour={bg} />;
  }
  return null;
}

function LogoBlock({ eventItem, logoPath, logoPadding, className }) {
  const logoSrc = resolveLogoSrc(eventItem, logoPath);
  if (!logoSrc) return null;
  return (
    <Box mb={2} className={className}>
      <Suspense fallback={null}>
        <Logo
          src={logoSrc}
          title="headerTitle"
          logoPadding={logoPadding}
          backgroundColor="#fff"
        />
      </Suspense>
    </Box>
  );
}


const EventPasswordGate = ({ event, eventId, onSuccess }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [open] = useState(true);

  const eventItem = event;
  const derived = useMemo(() => {
    if (!eventItem) {
      return { logoPadding: "10px", logoPath: null };
    }
    return {
      logoPadding: computeLogoPadding(eventItem),
      logoPath: findWebsiteLogoPath(eventItem),
    };
  }, [eventItem]);

  const handleClose = useCallback((_, reason) => {
    if (reason !== "backdropClick") return;
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const trimmed = (password || "").trim();
    if (!trimmed) {
      setError("Please enter the event password.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/public/api/event/checkEventPassword`,
        { password: trimmed, eventID: eventId }
      );
      console.log('paswordEventCheck',res.data)
      if (res.data?.success === true) {
        onSuccess();
        return;
      } 
      setError("Invalid password.");
    } catch (err) {
      const message =
        err.response?.data?.error ||
        (typeof err.response?.data === "string" ? err.response.data : null) ||
        err.message ||
        "Something went wrong. Please try again.";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const shouldShowTopLogo = eventItem?._id !== SPECIAL_HIDE_LOGO_EVENT_ID;

  return (
    <>
      <Background eventItem={eventItem} />
      <CssBaseline />
      <Container maxWidth="xl" className="mainContainer">
        <Grid item xs={12} id="firstRow" className="whiteBg" />

        <Dialog
          open={open}
          onClose={handleClose}
          aria-labelledby="form-dialog-title"
          disableEscapeKeyDown
        >
          {!eventItem ? null : (
            <>
              {shouldShowTopLogo && (
                <Box style={{ display: "flex", justifyContent: "center" }}>
                  <LogoBlock
                    eventItem={eventItem}
                    logoPath={derived.logoPath}
                    logoPadding={derived.logoPadding}
                    className={null}
                  />
                </Box>
              )}

              <Box px={3} pb={3} pt={shouldShowTopLogo ? 0 : 3}>
                <Typography className="title branding-title" align="center" gutterBottom>
                  This event is password protected
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  align="center"
                  className="description branding-title-upcoming"
                  style={{ marginBottom: "1.5rem" }}
                >
                  Enter the event password to continue.
                </Typography>
                <form onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Event password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    variant="outlined"
                    margin="normal"
                    autoFocus
                    disabled={submitting}
                    error={Boolean(error)}
                    autoComplete="current-password"
                  />
                  {error && (
                    <Typography color="error" variant="body2" style={{ marginTop: 8 }}>
                      {error}
                    </Typography>
                  )}
                  <Box mt={2}>
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      color="primary"
                      disabled={submitting}
                    >
                      {submitting ? "Checking…" : "Continue"}
                    </Button>
                  </Box>
                </form>
              </Box>
            </>
          )}
        </Dialog>
      </Container>
    </>
  );
};

export default React.memo(EventPasswordGate);
